/**
 * useTour
 *
 * Thin hook that persists the "tour seen" flag to localStorage so the guided
 * product tour only auto-launches once per browser.  The flag can be reset
 * (e.g. for testing) by calling resetTour().
 */

const STORAGE_KEY = 'cmmi_tour_seen'

/**
 * The TourProvider's `beforeClose` runs in the Tour component's unmount cleanup, so a
 * programmatic `setIsOpen(false)` marks the tour seen exactly like a user dismissal.
 * When the viewport drops below `lg` mid-tour the tour is force-closed, and that must
 * NOT consume the user's one auto-launch. This flag lets that one close pass silently.
 *
 * Only set it immediately before a close that is certain to happen (the tour is open),
 * otherwise it would swallow the next genuine dismissal instead.
 */
let suppressNextMarkSeen = false

export function suppressTourMarkSeenOnce(): void {
  suppressNextMarkSeen = true
}

export function useTour() {
  const hasSeen = (): boolean => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  }

  const markSeen = (): void => {
    if (suppressNextMarkSeen) {
      suppressNextMarkSeen = false
      return
    }
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch {
      // localStorage unavailable — silently ignore
    }
  }

  const resetTour = (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // localStorage unavailable — silently ignore
    }
  }

  return { hasSeen, markSeen, resetTour }
}
